import "./styles/app.css";
import { render } from "preact";
import { App } from "./ui/App";
import { DATA_BASE, dataHooks, loadCore, loadInsights, showCountry } from "./data/load";
import { API_BASE, decisions, filters, native, settings, ui, toast, type Palette, type Theme } from "./state/app";
import { collectionsFor } from "./data/collections";
import type { CountryId } from "./data/types";
import { bringToFront, rebuildDeck, setSpellings, upcoming } from "./state/deck";
import { seedTaste } from "./state/taste";
import { partnerSets, startSync } from "./state/partner";
import { consentChanged, count, initTelemetry } from "./state/telemetry";
import { noteVisit } from "./state/review";
import { overlayName, route } from "./state/router";

initTelemetry({
  consent: () => {
    const s = settings.get();
    return !s.usageAsked ? "ask" : s.shareUsage ? "yes" : "no";
  },
  country: () => settings.get().country,
  endpoint: `${API_BASE}/api/events`,
});
let usageSig = `${settings.get().usageAsked}|${settings.get().shareUsage}`;
settings.subscribe(() => {
  const sig = `${settings.get().usageAsked}|${settings.get().shareUsage}`;
  if (sig !== usageSig) {
    usageSig = sig;
    consentChanged();
  }
});

// in the claude.ai preview the host may choose a theme for the page; "Auto" follows it
const hostTheme = __PREVIEW__ ? (window.__nlHostTheme ?? null) : null;

function applyTheme(theme: Theme, palette: Palette): void {
  const root = document.documentElement;
  const t = theme === "system" ? hostTheme : theme;
  if (t === "light" || t === "dark") root.setAttribute("data-theme", t);
  else root.removeAttribute("data-theme");
  root.setAttribute("data-palette", palette);
  const dark = t === "dark" || (t !== "light" && matchMedia("(prefers-color-scheme: dark)").matches);
  // the browser bar takes the page's own background colour
  const bg = getComputedStyle(root).getPropertyValue("--bg").trim();
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", bg || (dark ? "#1c1a22" : "#f1ecfa"));
  native.theme?.(dark);
}

applyTheme(settings.get().theme, settings.get().palette);

// a screen very narrow for its text (a phone at 200% zoom, or the iPhone's larger text sizes,
// which native.ts records) wraps rows and drops their extras (app.css, .narrow)
function fitWidth(): void {
  const zoom = Number(document.documentElement.dataset.textZoom) || 1;
  document.documentElement.classList.toggle("narrow", window.innerWidth / zoom <= 300);
}
fitWidth();
window.addEventListener("resize", fitWidth);

// each screen names itself in the page title, for browser tabs, history and screen readers
const HOME_TITLE = document.title;
const SCREEN_TITLES = { list: "Shortlist", explore: "Explore", together: "Together", settings: "Settings", filters: "Filters", nameoff: "Name-off", chosen: "Your chosen name" };
function applyTitle(): void {
  const r = route.get();
  const k = overlayName(r.overlay);
  const what = k
    ? ui.get().core?.byKey.get(k)?.name
    : typeof r.overlay === "string"
      ? SCREEN_TITLES[r.overlay]
      : r.tab === "swipe"
        ? null
        : SCREEN_TITLES[r.tab];
  document.title = what ? `${what} · Nameling` : HOME_TITLE;
}
if (!__NATIVE__) {
  applyTitle();
  route.subscribe(applyTitle);
  ui.subscribe(applyTitle);
}

// the in-app Reduce motion setting stills the CSS animations too (app.css)
const applyMotion = () => document.documentElement.toggleAttribute("data-reduce-motion", settings.get().reduceMotion);
applyMotion();
settings.subscribe(applyMotion);
matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => applyTheme(settings.get().theme, settings.get().palette));

let lastTheme = `${settings.get().theme}|${settings.get().palette}`;
let deckSig = "";
function settingsSig(): string {
  const s = settings.get();
  return `${s.sex}|${s.partnerFirst}|${s.hidePartnerNopes}|${s.seed}`;
}
settings.subscribe(() => {
  const s = settings.get();
  if (`${s.theme}|${s.palette}` !== lastTheme) {
    lastTheme = `${s.theme}|${s.palette}`;
    applyTheme(s.theme, s.palette);
  }
  const sig = settingsSig();
  const core = ui.get().core;
  if (core && sig !== deckSig) {
    deckSig = sig;
    rebuildDeck(core);
  }
});

filters.subscribe(() => {
  const core = ui.get().core;
  if (core) rebuildDeck(core);
});

// partner data refreshes every few seconds; only rebuild the deck when their lists changed,
// and never swap out the card that's currently on top
let partnerSig = "";
partnerSets.subscribe(() => {
  const core = ui.get().core;
  const p = partnerSets.get();
  const sig = `${p.liked.size}:${p.nopes.size}:${p.others.map((m) => m.updatedAt).join(",")}`;
  if (!core || sig === partnerSig) return;
  partnerSig = sig;
  const top = upcoming(core, 1)[0]?.key;
  rebuildDeck(core);
  const newTop = upcoming(core, 1)[0]?.key;
  if (top && newTop !== top) bringToFront(top);
});

/** Keep this country's names on the device, so the app opens offline next time. */
function keepOffline(country: CountryId): void {
  // the preview has no service worker, and the iOS app carries every name already
  if (__PREVIEW__ || __NATIVE__ || !("caches" in window)) return;
  const url = new URL(`${DATA_BASE}/${country}/core.json`, location.href).href;
  caches
    .open("nameling-data")
    .then(async (c) => {
      if (!(await c.match(url))) await c.add(url);
    })
    .catch(() => {});
}

// each country has its own data; switching reloads it (swipes are kept: they're by name)
let loading: CountryId | null = null;
let synced = false;
function load(country: CountryId): void {
  loading = country;
  const prev = ui.get().core;
  // keep showing the current country's names until the new ones arrive
  if (prev) ui.set({ switching: country });
  else ui.set({ core: null, loadError: null });
  loadCore(country)
    .then((core) => {
      if (loading !== country) return; // switched again while this loaded
      showCountry(country);
      deckSig = settingsSig();
      const p = partnerSets.get();
      partnerSig = `${p.liked.size}:${p.nopes.size}:${p.others.map((m) => m.updatedAt).join(",")}`;
      // a collection or list from another country's Explore page may not exist here
      const f = filters.get();
      const stale = f.collection && f.collection !== "partner" && !collectionsFor(core).some((c) => c.id === f.collection);
      const otherList = f.list?.country && f.list.country !== core.country.id;
      if (stale || otherList) filters.set({ collection: stale ? null : f.collection, list: otherList ? null : f.list });
      ui.set({ core, switching: null });
      setSpellings(null);
      rebuildDeck(core);
      void seedTaste(core);
      // spellings of one name are dealt once: the families come with the trends, a moment later
      loadInsights()
        .then((ins) => {
          if (loading !== country) return;
          setSpellings(ins);
          const top = upcoming(core, 1)[0]?.key;
          rebuildDeck(core);
          if (top && upcoming(core, 1)[0]?.key !== top) bringToFront(top);
        })
        .catch(() => {});
      keepOffline(country);
      if (prev) toast(`Now showing names from ${core.country.name}`);
      if (!synced) {
        synced = true;
        startSync();
        count("open");
        noteVisit();
      }
    })
    .catch((e) => {
      console.error(e);
      if (loading !== country) return;
      const msg = navigator.onLine
        ? "Couldn't load the names. Please try again."
        : "You're offline, and these names haven't been saved on this device yet.";
      if (prev) {
        // stay where we were
        loading = prev.country.id;
        ui.set({ switching: null });
        settings.set({ country: prev.country.id });
        toast(msg);
      } else ui.set({ loadError: msg });
    });
}
load(settings.get().country);
settings.subscribe(() => {
  const c = settings.get().country;
  if (c !== loading) load(c);
});

render(<App />, document.getElementById("app")!);

/** Load the newest version. Swipes and settings are kept: they live in this browser's storage. */
function refresh(): void {
  const waiting = swReg?.waiting;
  if (!waiting) return location.reload();
  navigator.serviceWorker.addEventListener("controllerchange", () => location.reload());
  waiting.postMessage({ type: "SKIP_WAITING" });
}

let swReg: ServiceWorkerRegistration | undefined;
if (!__PREVIEW__ && !__NATIVE__ && "serviceWorker" in navigator && !/^(localhost|127\.)/.test(location.hostname)) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js")
      .then((reg) => {
        swReg = reg;
        reg.addEventListener("updatefound", () => {
          const next = reg.installing;
          next?.addEventListener("statechange", () => {
            if (next.state === "installed" && navigator.serviceWorker.controller)
              toast("A new version of Nameling is ready", { label: "Refresh", run: refresh }, 10000);
          });
        });
      })
      .catch(() => {});
  });
}

// a page left open across an update asks for data files the site no longer has
let outdatedShown = false;
dataHooks.outdated = () => {
  if (outdatedShown) return;
  outdatedShown = true;
  toast("Nameling has been updated. Refresh to carry on; your names are kept.", { label: "Refresh", run: refresh }, 30000);
};

/**
 * Ask the browser to keep Nameling's storage even when the device runs low on space. Chrome, Edge
 * and Safari decide by themselves, without asking; Firefox would show a prompt, so it's left out.
 */
function keepStorage(): void {
  if (__PREVIEW__ || __NATIVE__ || !navigator.storage?.persist || /firefox/i.test(navigator.userAgent)) return;
  navigator.storage
    .persisted()
    .then((done) => done || navigator.storage.persist())
    .catch(() => {});
}
// once there's something worth keeping
if (Object.keys(decisions.get().map).length) keepStorage();
else {
  const stop = decisions.subscribe(() => {
    stop();
    keepStorage();
  });
}

console.info(`Nameling ${typeof __BUILD_ID__ === "string" ? __BUILD_ID__ : "dev"}`);
